import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';

const Wishlist = () => {
  const [groupedItems, setGroupedItems] = useState([]);
  const [selectedIdsByShop, setSelectedIdsByShop] = useState({});
  const [activeShopId, setActiveShopId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchWishlist = async () => {
    try {
      const res = await axios.get('/api/wishlist');
      const groups = res.data.wishlist_by_shop || [];
      setGroupedItems(groups);

      const initialSelected = {};
      groups.forEach(group => {
        initialSelected[group.shop_id] = [];
      });
      setSelectedIdsByShop(initialSelected);
      setActiveShopId(null);
    } catch (err) {
      console.error("위시리스트 로드 실패:", err);
      if (err.response?.status === 401) {
        alert("로그인이 필요합니다.");
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWishlist();
  }, []);

  const getSelectedIds = (shopId) => selectedIdsByShop[shopId] || [];

  const recalcActiveShop = (nextState) => {
    const shopIds = Object.keys(nextState);
    for (const shopId of shopIds) {
      if ((nextState[shopId] || []).length > 0) {
        return Number(shopId);
      }
    }
    return null;
  };

  const isShopDisabled = (shopId) => {
    return activeShopId !== null && Number(activeShopId) !== Number(shopId);
  };

  const handleSelectAllByShop = (shopId, items, checked) => {
    if (isShopDisabled(shopId)) return;

    const nextState = {
      ...selectedIdsByShop,
      [shopId]: checked ? items.map(item => item.id) : []
    };

    setSelectedIdsByShop(nextState);
    setActiveShopId(recalcActiveShop(nextState));
  };

  const handleSelectItem = (shopId, itemId) => {
    if (isShopDisabled(shopId)) return;

    const current = selectedIdsByShop[shopId] || [];
    const next = current.includes(itemId)
      ? current.filter(id => id !== itemId)
      : [...current, itemId];

    const nextState = {
      ...selectedIdsByShop,
      [shopId]: next
    };

    setSelectedIdsByShop(nextState);
    setActiveShopId(recalcActiveShop(nextState));
  };

  const handleRemoveOne = async (id, shopId) => {
    if (!window.confirm('찜에서 제거하시겠습니까?')) return;

    try {
      const res = await axios.post('/api/wishlist/remove', { remove_id: id });
      if (res.data.success) {
        const nextState = {
          ...selectedIdsByShop,
          [shopId]: (selectedIdsByShop[shopId] || []).filter(selectedId => selectedId !== id)
        };
        setSelectedIdsByShop(nextState);
        setActiveShopId(recalcActiveShop(nextState));
        fetchWishlist();
      } else {
        alert(res.data.message || '삭제 실패');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        alert("로그인이 필요합니다.");
        navigate('/login');
      } else {
        alert("삭제 실패");
      }
    }
  };

  const handleRemoveSelectedByShop = async (shopId) => {
    if (isShopDisabled(shopId)) return;

    const selectedIds = getSelectedIds(shopId);

    if (selectedIds.length === 0) {
      alert('삭제할 상품을 선택해주세요.');
      return;
    }

    if (!window.confirm('선택한 상품을 찜 목록에서 삭제하시겠습니까?')) return;

    try {
      const res = await axios.post('/api/wishlist/remove_selected', {
        selected_items: selectedIds
      });

      if (res.data.success) {
        alert(res.data.message);

        const nextState = {
          ...selectedIdsByShop,
          [shopId]: []
        };
        setSelectedIdsByShop(nextState);
        setActiveShopId(recalcActiveShop(nextState));
        fetchWishlist();
      } else {
        alert(res.data.message || '삭제 중 오류가 발생했습니다.');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        alert("로그인이 필요합니다.");
        navigate('/login');
      } else {
        alert("삭제 중 오류가 발생했습니다.");
      }
    }
  };

  const handleToCartByShop = async (shopId) => {
    if (isShopDisabled(shopId)) return;

    const selectedIds = getSelectedIds(shopId);

    if (selectedIds.length === 0) {
      alert('장바구니에 담을 상품을 선택해주세요.');
      return;
    }

    try {
      const res = await axios.post('/api/wishlist/to_cart', {
        selected_items: selectedIds
      });

      if (res.data.success) {
        if (window.confirm(`${res.data.message} 장바구니로 이동하시겠습니까?`)) {
          navigate('/cart');
        }
      } else {
        alert(res.data.message || '장바구니 담기 실패');
      }
    } catch (err) {
      const data = err.response?.data;

      if (err.response?.status === 401) {
        alert("로그인이 필요합니다.");
        navigate('/login');
        return;
      }

      if (err.response?.status === 409 && data?.require_confirm_replace) {
        const ok = window.confirm(data.message);
        if (!ok) return;

        try {
          const retryRes = await axios.post('/api/wishlist/to_cart', {
            selected_items: selectedIds,
            confirm_replace: 1
          });

          if (retryRes.data.success) {
            if (window.confirm(`${retryRes.data.message} 장바구니로 이동하시겠습니까?`)) {
              navigate('/cart');
            }
          } else {
            alert(retryRes.data.message || '장바구니 담기 실패');
          }
        } catch (retryErr) {
          alert(retryErr.response?.data?.message || '장바구니 담기 실패');
        }

        return;
      }

      alert(data?.message || '장바구니 담기 실패');
    }
  };

  if (loading) return <div className="container mt-5 text-center">로딩 중...</div>;

  const hasItems = groupedItems.some(group => (group.items || []).length > 0);

  return (
    <div className="container my-2 px-0" style={{ maxWidth: '600px', paddingTop: '20px' }}>
      <h2 className="mb-3 mx-3">위시리스트</h2>

      {!hasItems ? (
        <div className="alert text-secondary mx-2" style={{ background: '#f1f3f5', fontSize: '13px' }}>찜한 상품이 없습니다.</div>
      ) : (
        groupedItems.map((group) => {
          const items = group.items || [];
          const selectedIds = getSelectedIds(group.shop_id);
          const allChecked = items.length > 0 && selectedIds.length === items.length;
          const disabled = isShopDisabled(group.shop_id);

          return (
            <div
              key={group.shop_id}
              style={{
                ...styles.shopBlock,
                ...(disabled ? styles.shopBlockDisabled : {})
              }}
            >

              <div style={styles.selectAllWrapper}>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`selectAll-${group.shop_id}`}
                    checked={allChecked}
                    disabled={disabled}
                    onChange={(e) => handleSelectAllByShop(group.shop_id, items, e.target.checked)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`selectAll-${group.shop_id}`}
                    style={{ fontSize: '0.8rem' }}
                  >
                    전체 선택
                  </label>
                </div>

                <button
                  type="button"
                  onClick={() => handleRemoveSelectedByShop(group.shop_id)}
                  style={styles.selectDelete}
                  disabled={disabled}
                >
                  선택 삭제
                </button>
              </div>

              <div style={styles.cartWrapper}>
                {items.map((item) => (
                  <div key={item.id} style={styles.cartItem}>
                    <div className="form-check mt-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        disabled={disabled}
                        onChange={() => handleSelectItem(group.shop_id, item.id)}
                      />
                    </div>

                    <Link to={`/product/${item.id}`}>
                      <img
                        src={item.image_path ? `/static/${item.image_path}` : '/static/default_thumb.jpg'}
                        alt={item.name}
                        style={styles.thumbnail}
                      />
                    </Link>

                    <div style={styles.cartDetails}>
                      <div style={styles.topInfo}>
                        <div style={styles.itemName}>{item.name}</div>
                        <button
                          onClick={() => handleRemoveOne(item.id, group.shop_id)}
                          style={styles.deleteBtn}
                        >
                          <i className="bi bi-x-circle"></i>
                        </button>
                      </div>
                      <div style={styles.bottomInfo}>
                        {item.price.toLocaleString()}원
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={styles.fixedBottomLocal}>
                <button
                  onClick={() => handleToCartByShop(group.shop_id)}
                  className="btn btn-primary btn-sm"
                  disabled={disabled}
                  style={{
                    height: '40px',
                    padding: '0 20px',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: 500
                  }}
                >
                  선택 상품 장바구니 담기
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

const styles = {
  shopBlock: {
    background: 'white',
    marginBottom: '28px',
    padding: '0 18px'
  },
  shopBlockDisabled: {
    opacity: 0.45
  },
  shopHeader: {
    padding: '6px 0px 16px'
  },
  shopName: {
    fontWeight: '700',
    fontSize: '1rem',
    color: '#1c1c1c'
  },
  selectAllWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0 0px 0px'
  },
  selectDelete: {
    fontSize: '0.8rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#666'
  },
  cartWrapper: {
    background: '#fff',
    padding: '0px 0px 0px',
    marginBottom: '0'
  },
  cartItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '20px 0',
    borderTop: '1px solid #e0e0e0'
  },
  thumbnail: {
    width: '64px',
    height: '64px',
    objectFit: 'cover',
    borderRadius: '6px'
  },
  cartDetails: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  topInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  itemName: {
    fontWeight: '600',
    fontSize: '0.95rem',
    color: '#1c1c1c'
  },
  deleteBtn: {
    background: 'none',
    border: 'none',
    color: '#ddd',
    fontSize: '1.2rem',
    cursor: 'pointer'
  },
  bottomInfo: {
    fontWeight: '600',
    color: '#333',
    fontSize: '0.9rem'
  },
  fixedBottomLocal: {
    padding: '12px 0px 16px',
    textAlign: 'right',
    borderTop: '1px solid #e0e0e0',
    background: '#fff'
  }
};

export default Wishlist;